import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import { mdiAlertOutline, mdiInformationOutline } from '@mdi/js'
import { cn } from '../../lib/utils'
import type { ImportGap } from './importTypes'
import { EmptyState } from '../ui/empty-state'

interface ImportGapsPanelProps {
    org: string
    importUid: string
    gaps: ImportGap[]
}

/**
 * Localizes one gap row. The regulatory rules are emitted per compliance
 * control (`missing_<control_code>`), so they resolve through one shared key
 * with the control name interpolated rather than needing an i18n entry per
 * control code.
 */
function useGapLabel() {
    const { t } = useTranslation('imports')

    return (gap: ImportGap): string => {
        if (gap.code.startsWith('missing_')) {
            const control = (gap.label || gap.code.slice('missing_'.length)).toUpperCase()
            return t('gaps.missing_regulatory', { control })
        }
        return t(`gaps.${gap.code}`, { defaultValue: gap.code })
    }
}

/**
 * The gaps panel (spec §7.4, right column): warning-tinted rows, each with the
 * count and a deep link built from the server-provided `filter` object, so a
 * new rule becomes navigable without a frontend change.
 */
export function ImportGapsPanel({ org, importUid, gaps }: ImportGapsPanelProps) {
    const { t } = useTranslation('imports')
    const gapLabel = useGapLabel()

    return (
        <section id="import-gaps" data-testid="import-gaps" className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">{t('gaps.title')}</h2>

            {gaps.length === 0 ? (
                <EmptyState title={t('gaps.empty')} />
            ) : (
                <ul className="space-y-2">
                    {gaps.map(gap => {
                        const isWarning = gap.severity === 'warning'
                        const entity = typeof gap.filter?.entity === 'string' ? gap.filter.entity : undefined

                        return (
                            <li
                                key={gap.code}
                                data-testid={`import-gap-${gap.code}`}
                                className={cn(
                                    'flex items-center justify-between gap-3 rounded-lg border p-3',
                                    isWarning
                                        ? 'border-warning/25 bg-warning/10'
                                        : 'border-border bg-muted/40',
                                )}
                            >
                                <div className="flex min-w-0 items-center gap-2">
                                    <AppIcon
                                        path={isWarning ? mdiAlertOutline : mdiInformationOutline}
                                        size="md"
                                        className={isWarning ? 'text-warning' : 'text-muted-foreground'}
                                    />
                                    <span className="truncate text-sm text-foreground">{gapLabel(gap)}</span>
                                </div>

                                <div className="flex shrink-0 items-center gap-3">
                                    <span className="text-sm font-semibold tabular-nums text-foreground">
                                        {gap.count}
                                    </span>
                                    {entity === 'assets' && (
                                        <Link
                                            to="/$org/assets"
                                            params={{ org }}
                                            search={{
                                                q: undefined,
                                                flag: undefined,
                                                asset_type_code: undefined,
                                                asset_type_uid: undefined,
                                                showBuildingGroups: undefined,
                                                showBuildings: undefined,
                                                archived: undefined,
                                                import_uid: importUid,
                                            }}
                                            data-testid={`import-gap-${gap.code}-link`}
                                            className="text-xs font-medium text-primary hover:underline"
                                        >
                                            {t('gaps.view')}
                                        </Link>
                                    )}
                                    {entity === 'users' && (
                                        <Link
                                            to="/$org/users"
                                            params={{ org }}
                                            data-testid={`import-gap-${gap.code}-link`}
                                            className="text-xs font-medium text-primary hover:underline"
                                        >
                                            {t('gaps.view')}
                                        </Link>
                                    )}
                                    {entity === 'contracts' && (
                                        <Link
                                            to="/$org/contracts"
                                            params={{ org }}
                                            data-testid={`import-gap-${gap.code}-link`}
                                            className="text-xs font-medium text-primary hover:underline"
                                        >
                                            {t('gaps.view')}
                                        </Link>
                                    )}
                                </div>
                            </li>
                        )
                    })}
                </ul>
            )}
        </section>
    )
}
